import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const PERIODS = [
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const LLMUsageDashboard = () => {
  const [usage, setUsage] = useState({ periods: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [activePeriod, setActivePeriod] = useState('day');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/api/admin/llm/usage');
        if (!active) return;
        setUsage(data || { periods: {}, totals: {} });
      } catch {
        toast.error('Failed to load LLM usage');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const rows = useMemo(() => {
    const periodRows = usage?.periods?.[activePeriod] || [];
    return [...periodRows].sort((a, b) => (b.total_tokens || 0) - (a.total_tokens || 0));
  }, [usage, activePeriod]);

  const totals = usage?.totals?.[activePeriod] || { tokens_in: 0, tokens_out: 0, requests: 0 };

  if (loading) {
    return <p className="muted">Loading LLM usage...</p>;
  }

  return (
    <section className="admin-llm-usage">
      <h3>Token Usage</h3>
      <div className="project-tabs" role="tablist" aria-label="LLM usage periods">
        {PERIODS.map((period) => (
          <button
            key={period.key}
            type="button"
            className={`tab-btn ${activePeriod === period.key ? 'active' : ''}`}
            onClick={() => setActivePeriod(period.key)}
            aria-controls="llm-usage-panel"
            aria-expanded={activePeriod === period.key}
          >
            {period.label}
          </button>
        ))}
      </div>

      <div id="llm-usage-panel" className="admin-usage-summary">
        <p><strong>Requests:</strong> {totals.requests || 0}</p>
        <p><strong>Tokens In:</strong> {totals.tokens_in || 0}</p>
        <p><strong>Tokens Out:</strong> {totals.tokens_out || 0}</p>
        <p><strong>Total Tokens:</strong> {(totals.tokens_in || 0) + (totals.tokens_out || 0)}</p>
      </div>

      {rows.length === 0 ? (
        <p className="muted">No LLM activity recorded yet.</p>
      ) : (
        <div className="admin-usage-table-wrap">
          <table className="admin-usage-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Provider</th>
                <th>Requests</th>
                <th>Tokens In</th>
                <th>Tokens Out</th>
                <th>Total Tokens</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.provider}-${row.model}`}>
                  <td>{row.model}</td>
                  <td>{row.provider}</td>
                  <td>{row.requests || 0}</td>
                  <td>{row.tokens_in || 0}</td>
                  <td>{row.tokens_out || 0}</td>
                  <td>{row.total_tokens || (row.tokens_in || 0) + (row.tokens_out || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default LLMUsageDashboard;
